import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Listing } from '../api';
import DeliveryMap from '../components/DeliveryMap';
import { addressStreetLineOnly, extractPostalCodeFromAddress } from '../utils/addressUtils';
import {
  calculateCheckoutTotal,
  calculateSalesTaxAmount,
  resolveTaxStateIos,
} from '../utils/salesTax';

const OrderConfirmation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { listing, deliveryAddress, deliveryTime } = location.state || {};

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">No order information found</p>
          <Link to="/" className="text-green-600 hover:text-green-700 hover:underline">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number | null) => {
    if (!price) return 'Free';
    return `$${price.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">✅</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Order Placed!</h1>
            <p className="text-gray-600">Your order has been confirmed</p>
          </div>

          <div className="border-t border-b py-6 my-6">
            <div className="flex items-center justify-center space-x-4 mb-4">
              {listing.image_url ? (
                <img
                  src={listing.image_url}
                  alt={listing.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
              ) : (
                <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                  <span className="text-4xl">📦</span>
                </div>
              )}
              <div className="text-left">
                <h2 className="text-xl font-semibold">{listing.title}</h2>
                <div className="space-y-2">
                  {(() => {
                    const itemPrice = Number(listing.price || 0);
                    const shippingFee = Number(listing.shipping_fee) || 2.99;
                    const addr = location.state?.addressData as {
                      state?: string;
                      postal_code?: string;
                    } | undefined;
                    const deliveryAddr = (location.state?.deliveryAddress as string) || '';
                    const taxState = resolveTaxStateIos(
                      addr?.state || '',
                      null,
                      extractPostalCodeFromAddress(addr, deliveryAddr)
                    );
                    const salesTax = calculateSalesTaxAmount(
                      itemPrice,
                      shippingFee,
                      taxState,
                      !!deliveryAddr.trim()
                    );
                    const total = calculateCheckoutTotal(itemPrice, shippingFee, salesTax);
                    return (
                      <>
                        <p className="text-sm text-gray-600">💳 Paid: ${total.toFixed(2)}</p>
                        <p className="text-xs text-gray-500 ml-4">- Item: {formatPrice(listing.price)}</p>
                        <p className="text-xs text-gray-500 ml-4">- Delivery: ${shippingFee.toFixed(2)}</p>
                        <p className="text-xs text-gray-500 ml-4">- Tax: ${salesTax.toFixed(2)}</p>
                        <p className="text-lg font-bold text-gray-900 mt-2">Total: ${total.toFixed(2)}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-left mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Delivery Information</h3>
              <p className="text-sm text-gray-600">
                {addressStreetLineOnly(null, deliveryAddress || '')}
              </p>
            </div>

            {deliveryTime && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">🚚</span>
                  <div>
                    <p className="font-semibold text-green-800">Estimated Delivery</p>
                    <p className="text-green-700">{deliveryTime}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Seller:</strong> {listing.seller_name}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                The seller will contact you shortly to confirm delivery details.
              </p>
            </div>

            {location.state?.paymentMethod === 'card' && (
              <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300">
                <p className="text-sm font-semibold text-yellow-900 mb-2">
                  💵 Cash Payment on Delivery
                </p>
                <p className="text-sm text-yellow-800">
                  Please have <strong>${(Number(listing.price || 0)).toFixed(2)}</strong> in cash ready when the item is delivered. 
                  The delivery and service fees have already been paid with your card.
                </p>
              </div>
            )}

            <div className="mt-4">
              <h3 className="font-semibold mb-2">Delivery Map</h3>
              <DeliveryMap
                deliveryAddress={deliveryAddress}
                status="pending"
                orderDate={new Date().toISOString()}
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-6 rounded-lg transition"
            >
              Continue Shopping
            </button>
            <Link
              to="/my-orders"
              className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-6 rounded-lg transition text-center"
            >
              View My Orders
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;

